import { useEffect, useRef } from "react";

const CORNERS_3D = [
    [-1, -1, -1],
    [1, -1, -1],
    [1, 1, -1],
    [-1, 1, -1],
    [-1, -1, 1],
    [1, -1, 1],
    [1, 1, 1],
    [-1, 1, 1],
];

const FACES = [
    { idx: [4, 5, 6, 7], n: [0, 0, 1] },
    { idx: [1, 0, 3, 2], n: [0, 0, -1] },
    { idx: [5, 1, 2, 6], n: [1, 0, 0] },
    { idx: [0, 4, 7, 3], n: [-1, 0, 0] },
    { idx: [3, 2, 6, 7], n: [0, 1, 0] },
    { idx: [0, 1, 5, 4], n: [0, -1, 0] },
];

const FLEE_RADIUS = 200;

const PALETTES = {
    green: "96,165,250",
    ivory: "255,255,255",
};

const LIGHT = (() => {
    const l = [0.45, -0.55, 0.9];
    const len = Math.hypot(...l);
    return l.map((v) => v / len);
})();

export default function PixelField() {
    const ref = useRef(null);

    useEffect(() => {
        const canvas = ref.current;
        const ctx = canvas.getContext("2d");
        let w = 0;
        let h = 0;
        let raf;
        let scroll = window.scrollY;
        let smooth = scroll;
        const mouse = { x: -9999, y: -9999 };

        const resize = () => {
            w = canvas.width = window.innerWidth;
            h = canvas.height = window.innerHeight;
        };
        const onScroll = () => {
            scroll = window.scrollY;
        };
        resize();
        window.addEventListener("resize", resize);
        window.addEventListener("scroll", onScroll, { passive: true });
        const onMove = (e) => { mouse.x = e.clientX; mouse.y = e.clientY; };
        const onLeave = () => { mouse.x = -9999; mouse.y = -9999; };
        window.addEventListener("mousemove", onMove, { passive: true });
        window.addEventListener("mouseout", onLeave);

        const parts = Array.from({ length: 80 }, () => ({
            x: Math.random() * 2 - 1,
            y: Math.random() * 2 - 1,
            z: Math.random() * 2 - 1,
            hs: Math.random() * 0.016 + 0.012,
            spinX: (Math.random() * 0.35 + 0.15) * (Math.random() > 0.5 ? 1 : -1),
            spinY: (Math.random() * 0.35 + 0.15) * (Math.random() > 0.5 ? 1 : -1),
            o: Math.random() * 0.35 + 0.6,
            green: Math.random() > 0.4,
            phase: Math.random() * Math.PI * 2,
            ox: 0, oy: 0, vx: 0, vy: 0,
        }));

        let t = Math.random() * 100;
        const draw = () => {
            t += 0.0045;
            smooth += (scroll - smooth) * 0.08;
            ctx.clearRect(0, 0, w, h);

            const rotY = smooth * 0.0012;
            const rotX = smooth * 0.0005;
            const gy = Math.cos(rotY);
            const gyS = Math.sin(rotY);
            const gx = Math.cos(rotX);
            const gxS = Math.sin(rotX);
            const R = Math.min(w, h) * 0.62;
            const fov = 3.1;
            const cxp = w / 2;
            const cyp = h / 2;

            const faceList = [];

            for (const p of parts) {
                const lx = t * p.spinX + p.phase;
                const ly = t * p.spinY + p.phase * 1.7;
                const clx = Math.cos(lx);
                const slx = Math.sin(lx);
                const cly = Math.cos(ly);
                const sly = Math.sin(ly);

                const proj = [];
                let visible = true;
                for (const [cx0, cy0, cz0] of CORNERS_3D) {
                    const x0 = cx0 * p.hs;
                    const y0 = cy0 * p.hs;
                    const z0 = cz0 * p.hs;
                    const y1 = y0 * clx - z0 * slx;
                    const z1 = y0 * slx + z0 * clx;
                    const x2 = x0 * cly + z1 * sly;
                    const z2 = -x0 * sly + z1 * cly;

                    const wx = p.x + x2;
                    const wy = p.y + y1;
                    const wz = p.z + z2;

                    const rx = wx * gy - wz * gyS;
                    let rz = wx * gyS + wz * gy;
                    const ry = wy * gx - rz * gxS;
                    rz = wy * gxS + rz * gx;

                    const depth = rz + 1.6;
                    const persp = fov / (fov + depth);
                    if (persp <= 0.2) {
                        visible = false;
                        break;
                    }
                    proj.push([cxp + rx * R * persp, cyp + ry * R * persp, depth]);
                }
                if (!visible) continue;

                const scx = (proj[0][0] + proj[6][0]) / 2 + p.ox;
                const scy = (proj[0][1] + proj[6][1]) / 2 + p.oy;
                const dx = scx - mouse.x;
                const dy = scy - mouse.y;
                const dist = Math.hypot(dx, dy);
                if (dist < FLEE_RADIUS) {
                    const f = ((FLEE_RADIUS - dist) / FLEE_RADIUS) ** 2;
                    const inv = dist > 0.001 ? 1 / dist : 0;
                    p.vx += dx * inv * f * 4.2 + (Math.random() - 0.5) * f * 5;
                    p.vy += dy * inv * f * 4.2 + (Math.random() - 0.5) * f * 5;
                }
                p.vx *= 0.9;
                p.vy *= 0.9;
                p.ox += p.vx;
                p.oy += p.vy;
                p.ox *= 0.985;
                p.oy *= 0.985;

                for (const face of FACES) {
                    let [nx, ny, nz] = face.n;
                    const ny1 = ny * clx - nz * slx;
                    const nz1 = ny * slx + nz * clx;
                    const nx2 = nx * cly + nz1 * sly;
                    const nz2 = -nx * sly + nz1 * cly;
                    const rxn = nx2 * gy - nz2 * gyS;
                    let rzn = nx2 * gyS + nz2 * gy;
                    const ryn = ny1 * gx - rzn * gxS;
                    rzn = ny1 * gxS + rzn * gx;

                    const dot = rxn * LIGHT[0] + ryn * LIGHT[1] + rzn * LIGHT[2];
                    const shade = 0.22 + 0.78 * ((dot + 1) / 2);

                    let depth = 0;
                    const pts = face.idx.map((ci) => {
                        depth += proj[ci][2];
                        return [proj[ci][0] + p.ox, proj[ci][1] + p.oy];
                    });

                    faceList.push({
                        pts,
                        depth: depth / 4,
                        green: p.green,
                        shade,
                        alpha: p.o,
                    });
                }
            }

            faceList.sort((a, b) => b.depth - a.depth);

            for (const f of faceList) {
                const col = f.green ? PALETTES.green : PALETTES.ivory;
                const near = Math.max(0, Math.min(1, (2.8 - f.depth) / 2.8));
                const fillA = f.alpha * (0.14 + f.shade * 0.3) * (0.5 + near * 0.5);
                const lineA = f.alpha * (0.18 + f.shade * 0.28);

                ctx.beginPath();
                ctx.moveTo(f.pts[0][0], f.pts[0][1]);
                ctx.lineTo(f.pts[1][0], f.pts[1][1]);
                ctx.lineTo(f.pts[2][0], f.pts[2][1]);
                ctx.lineTo(f.pts[3][0], f.pts[3][1]);
                ctx.closePath();
                ctx.fillStyle = `rgba(${col},${fillA})`;
                ctx.fill();
                ctx.lineWidth = 3;
                ctx.strokeStyle = `rgba(${col},${lineA * 0.25})`;
                ctx.stroke();
                ctx.lineWidth = 1.2;
                ctx.strokeStyle = `rgba(${col},${lineA})`;
                ctx.stroke();
            }
            raf = requestAnimationFrame(draw);
        };
        raf = requestAnimationFrame(draw);

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", resize);
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseout", onLeave);
        };
    }, []);

    return <canvas ref={ref} aria-hidden="true" className="pointer-events-none fixed inset-0 z-0" />;
}
